// @ts-nocheck

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';

type Props = {
  className?: string;
  url: string;
  rowCount: number;
  columnCount: number;
  width?: number;
  height?: number;
  duration?: number;
  keepRatio?: boolean;
  loop?: boolean;
  onDone?: (...params: unknown) => unknown;
  play?: number;
  expires?: boolean;
};

const loadImage = (url = '', callback) => {
  const img = new Image();
  img.onload = () => {
    callback(null, img);
  };
  img.onerror = (err) => {
    callback(err);
  };
  img.src = url;
};

const Spritesheet = ({
  className,
  url,
  rowCount,
  columnCount,
  width,
  height,
  duration,
  keepRatio,
  loop,
  onDone,
  play,
  expires,
}: Props) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const [spriteSize, setSpriteSize] = useState({ width: 0, height: 0 });
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [fps, setFps] = useState(1000 / 60);
  const [triggerPlayDate, setTriggerPlayDate] = useState(null);

  const req = useRef(null);
  const domItem = useRef(null);
  const prevInterval = useRef(null);
  const currentFrame = useRef(0);

  const totalFrame = useMemo(() => rowCount * columnCount, [columnCount, rowCount]);

  const reset = useCallback(() => {
    if (req.current) {
      cancelAnimationFrame(req.current);
    }
    prevInterval.current = performance.now();
    currentFrame.current = 0;
  }, []);

  const getSpritePosition = useCallback(() => {
    const colIdx = currentFrame.current % columnCount;
    const rowIdx = Math.floor(currentFrame.current / columnCount);
    const _width = -frameSize.width * colIdx;
    const _height = -frameSize.height * rowIdx;
    return `${_width}px ${_height}px`;
  }, [columnCount, frameSize]);

  const onAnimationFrame = useCallback(
    (timestamp) => {
      if (isLoaded) {
        if (!loop) {
          // Si c'est trop tard pour jouer l'animation (au dela de 30 secondes après le déclenchement + durée)
          // (et qu'on est pas en loop)
          const isExpired = triggerPlayDate && triggerPlayDate + duration + 30000 < Date.now();
          const isLastFrame = currentFrame.current === totalFrame - 1;

          if (isLastFrame || isExpired) {
            if (onDone) {
              onDone();
            }
            reset();
            return;
          }
        }
        if (prevInterval.current) {
          const delta = timestamp - prevInterval.current;
          if (delta > fps) {
            prevInterval.current = timestamp;
            currentFrame.current = (currentFrame.current + 1) % totalFrame;
          }
        }
        const pos = getSpritePosition();
        if (domItem.current) {
          domItem.current.style.backgroundPosition = pos;
        }
      }
      req.current = requestAnimationFrame(onAnimationFrame);
    },
    [isLoaded, triggerPlayDate, duration, totalFrame, getSpritePosition, onDone, reset, loop, fps],
  );

  useEffect(() => {
    setIsLoaded(false);
    if (url) {
      loadImage(url, (err, img) => {
        if (err) {
          console.warn(`Unable to load spritesheet: ${url}  Reason: ${err}`);
        }

        if (img) {
          setIsLoaded(true);
          setSpriteSize({
            width: img.width,
            height: img.height,
          });

          if (domItem.current) {
            domItem.current.style.backgroundImage = `url(${url})`;
          }

          prevInterval.current = performance.now();
        }
      });
    }
  }, [url]);

  useEffect(() => {
    if (duration) {
      setFps(duration / totalFrame);
    }
  }, [duration, totalFrame]);

  useEffect(() => {
    if (isLoaded && spriteSize.width && spriteSize.height) {
      let w = width || Math.floor(spriteSize.width / columnCount);
      let h = height || Math.floor(spriteSize.height / rowCount);

      if (keepRatio) {
        const ratio = spriteSize.width / columnCount / (spriteSize.height / rowCount);

        if (width && height) {
          const currentRatio = width / height;
          console.warn(
            `You specified a width and a height with a wrong ratio (${currentRatio}). The real ratio is ${ratio}. Adjusting height to keep the ratio.`,
          );
        }
        if (width) {
          h = width / ratio;
        } else if (height) {
          w = height * ratio;
        }
      }

      if (domItem.current) {
        domItem.current.style.width = `${w}px`;
        domItem.current.style.height = `${h}px`;
        domItem.current.style.backgroundSize = `${w * columnCount}px ${h * rowCount}px`;
      }

      setFrameSize({ width: w, height: h });
    }
  }, [columnCount, width, height, isLoaded, keepRatio, rowCount, spriteSize.height, spriteSize.width]);

  useEffect(() => {
    if (play) {
      if (expires) {
        setTriggerPlayDate(Date.now());
      }

      req.current = requestAnimationFrame(onAnimationFrame);
    }

    return () => {
      if (req.current) {
        cancelAnimationFrame(req.current);
      }

      setTriggerPlayDate(null);
    };
  }, [expires, onAnimationFrame, play]);

  return <div className={className} ref={domItem} data-testid="spriteSheet" />;
};

export default Spritesheet;
